/**
 * @file DraggableDaysList.jsx
 * @module packages/days/src/components/DraggableDaysList
 * @description This module defines the DraggableDaysList component, which is responsible for
 * rendering a list of individual day items. Each item is draggable using `react-beautiful-dnd`
 * and displays the day number (as a link to the DayDetailPage), date, summary, and action buttons
 * (edit, delete).
 *
 * This component is a child of `DaysList.jsx` and receives the list of `days`,
 * callbacks for `openEditModal` and `handleDeleteDay`, and the `tripId` (to construct links
 * to the `DayDetailPage`).
 *
 * It uses:
 * - `react-beautiful-dnd` for `Droppable` and `Draggable` capabilities.
 * - `Link` from `react-router-dom` to navigate to the `DayDetailPage`.
 * - `formatDate` from `@trip-planner-monorepo/shared` for displaying dates.
 * - `FaEdit`, `FaTrash` icons from `react-icons/fa`.
 */
import React from 'react';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { Link } from 'react-router-dom';
import { formatDate, ContentBlockRenderer } from '@trip-planner-monorepo/shared';
import { FaEdit, FaTrash } from 'react-icons/fa';
import './DraggableDaysList.css';

/**
 * @function DraggableDaysList
 * @description A React functional component that renders a list of draggable day items.
 * Each item displays day information and provides actions like edit and delete.
 * The day number is a link to the specific `DayDetailPage`.
 * It is memoized using `React.memo` for performance optimization, as it can render many items.
 * 
 * @param {Object} props - The component's props.
 * @param {Array<Object>} props.days - An array of day objects to render.
 * @param {Function} props.openEditModal - Callback to open the modal for editing a day.
 * @param {Function} props.handleDeleteDay - Callback to initiate the deletion of a day.
 * @param {string} props.tripId - The ID of the current trip, used to construct links to the DayDetailPage.
 * @returns {JSX.Element} The rendered list of draggable day items.
 */
const DraggableDaysList = React.memo(({ days, openEditModal, handleDeleteDay, tripId }) => {
  React.useEffect(() => {
    console.log('DraggableDaysList days:', days.map(d => ({ id: d.id, order: d.order })));
  }, [days]);

  return (
    <Droppable droppableId="days-list">
      {(droppableProvided, droppableSnapshot) => (
        <ul
          ref={droppableProvided.innerRef}
          {...droppableProvided.droppableProps}
          className={`day-list ${droppableSnapshot.isDraggingOver ? 'dragging-over' : ''}`}
        >
          {days.map((day, index) => {
            console.log(`Rendering day ${index}:`, day);
            return (
              <Draggable
                key={`day-${day.id}`}
                draggableId={`day-${day.id}`}
                index={index}
              >
                {(draggableProvided, draggableSnapshot) => {
                  if (draggableSnapshot.isDragging) {
                    console.log(`Dragging day ${day.id}`);
                  }

                  return (
                    <li
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      className={`day-item ${index % 2 === 0 ? 'even' : 'odd'} ${draggableSnapshot.isDragging ? 'dragging' : ''
                        }`}
                    >
                      <div
                        {...draggableProvided.dragHandleProps}
                        className="drag-handle"
                        onMouseDown={() => console.log('Drag started for day:', day.id)}
                      >
                        ☰
                      </div>

                      <div className="day-content">
                        <div className="day-info">
                          <Link to={`/trips/${tripId}/day/${day.id}`} className="day-number-link">
                            <span className="day-number">Day {index + 1}</span>
                          </Link>
                          <span className="day-date">{day.date ? formatDate(day.date) : 'Date not set'}</span>
                        </div>
                        <div className="day-summary">
                          <ContentBlockRenderer
                            content={day.content?.filter(block => block.type !== 'document_reference') || []}
                            tripId={tripId}
                            className="day-summary-content"
                          />
                        </div>
                        <div className="day-actions">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openEditModal(day);
                            }}
                            className="action-button edit"
                            title="Edit day"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteDay(day.id);
                            }}
                            className="action-button delete"
                            title="Delete day"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                }}
              </Draggable>
            );
          })}
          {droppableProvided.placeholder}
        </ul>
      )}
    </Droppable>
  );
});

export default DraggableDaysList;